// ============================================================================
// The bench — who is ready to take a building.
//
// Since item #683 (2026-10-03) the bench is v_bench_ready: field subs at Ready who
// do nightly (recurring) work — read from v_subcontractor_work, the one home of
// that answer (TCSD-0368, TCSD-0373 ¶4; Ben: "the logic for bench is nightly/
// recurring is TRUE"). It reads the SUB's own record, so every employee can see it.
// The application detail (intake back, fit, crew size) comes from the sub's
// application record when there is one.
//
// How each bench sub was vetted (TCSD-0402, Ben 2026-10-03 "A") is read from what
// is already recorded — no approval field: their application is back; they are on
// the work records (worked for TCS before); or who moved them to the bench and
// when, from the change history (audit_log, readable by every employee — the same
// read the facility change log makes). The AM-approve tick of the retired drawer
// is gone; an AM moving a sub to the bench IS the vouch (TCSD-0373, TCSD-0170).
//
// Flat fetch, house style. No TanStack Query.
// ============================================================================
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { errorMessage } from '../../../shared/errors';

export interface BenchRow {
  provider_id: string;
  display_name: string;
  owner_name: string | null;
  market: string | null;
  phone: string | null;
  email: string | null;
  capabilities: string[];
  coverage_markets: string[];
  intake_completed: boolean;
  /** On the work records — has cleaned or done a project for TCS. */
  worked_before: boolean;
  /** Who last moved them to the bench, and when (from the change history). */
  benched_by: string | null;
  benched_at: string | null;
  fit_score: number | null;
  bench_depth: number | null;
  /** Days since the record last moved — how long they have been sitting there. */
  days_on_bench: number;
}

export function useSubBench(): {
  rows: BenchRow[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const [rows, setRows] = useState<BenchRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: e1 } = await supabase
          .from('v_bench_ready')
          .select('provider_id, display_name, phone, email, coverage_markets, capabilities, updated_at')
          .order('updated_at', { ascending: true });
        if (e1) throw e1;
        const bench = (data ?? []) as Array<{
          provider_id: string;
          display_name: string | null;
          phone: string | null;
          email: string | null;
          coverage_markets: string[] | null;
          capabilities: string[] | null;
          updated_at: string;
        }>;
        const ids = bench.map((b) => b.provider_id);
        const { data: apps, error: e2 } = ids.length
          ? await supabase
              .from('sourcing_prospects')
              .select('promoted_provider_id, owner_name, intake_completed, fit_score, bench_depth')
              .in('promoted_provider_id', ids)
          : { data: [], error: null };
        if (e2) throw e2;

        const [work, moves] = ids.length
          ? await Promise.all([
              supabase.from('v_subcontractor_work').select('provider_id, kind_source').in('provider_id', ids),
              supabase
                .from('audit_log')
                .select('record_id, action, changed_at, changed_by_employee_id, old_status:old_data->>provider_status')
                .eq('table_name', 'service_providers')
                .in('record_id', ids)
                .in('action', ['INSERT', 'UPDATE'])
                .eq('new_data->>provider_status', 'Ready')
                .order('changed_at', { ascending: false }),
            ])
          : [{ data: [], error: null }, { data: [], error: null }];
        if (work.error) throw work.error;
        if (moves.error) throw moves.error;
        // The latest move ONTO the bench per sub — a row that was already Ready
        // before the change (an edit of something else) is not a move.
        const moveBy = new Map<string, { eid: string | null; at: string }>();
        for (const m of (moves.data ?? []) as Array<{ record_id: string; action: string; changed_at: string; changed_by_employee_id: string | null; old_status: string | null }>) {
          if (moveBy.has(m.record_id)) continue;
          if (m.action === 'UPDATE' && m.old_status === 'Ready') continue;
          moveBy.set(m.record_id, { eid: m.changed_by_employee_id, at: m.changed_at });
        }
        const eids = [...new Set([...moveBy.values()].map((m) => m.eid).filter((e): e is string => Boolean(e)))];
        const { data: emps, error: e3 } = eids.length
          ? await supabase.from('employees').select('employee_id, employee_name_first, employee_name_last').in('employee_id', eids)
          : { data: [], error: null };
        if (e3) throw e3;
        if (cancelled) return;
        type App = { promoted_provider_id: string; owner_name: string | null; intake_completed: boolean; fit_score: number | null; bench_depth: number | null };
        const appBy = new Map(((apps ?? []) as App[]).map((a) => [a.promoted_provider_id, a]));
        const workedBefore = new Set(
          ((work.data ?? []) as Array<{ provider_id: string; kind_source: string }>).filter((w) => w.kind_source === 'records').map((w) => w.provider_id),
        );
        const nameOf = new Map(
          ((emps ?? []) as Array<{ employee_id: string; employee_name_first: string | null; employee_name_last: string | null }>).map((e) => [
            e.employee_id,
            [e.employee_name_first, e.employee_name_last].filter(Boolean).join(' ') || e.employee_id,
          ]),
        );

        const now = Date.now();
        setRows(
          bench.map((r) => {
            const a = appBy.get(r.provider_id);
            return {
              provider_id: r.provider_id,
              display_name: r.display_name || r.provider_id,
              owner_name: a?.owner_name ?? null,
              market: r.coverage_markets?.[0] ?? null,
              phone: r.phone,
              email: r.email,
              capabilities: r.capabilities ?? [],
              coverage_markets: r.coverage_markets ?? [],
              intake_completed: Boolean(a?.intake_completed),
              worked_before: workedBefore.has(r.provider_id),
              benched_by: (() => {
                const m = moveBy.get(r.provider_id);
                return m?.eid ? (nameOf.get(m.eid) ?? null) : null;
              })(),
              benched_at: moveBy.get(r.provider_id)?.at ?? null,
              fit_score: a?.fit_score === null || a?.fit_score === undefined ? null : Number(a.fit_score),
              bench_depth: a?.bench_depth === null || a?.bench_depth === undefined ? null : Number(a.bench_depth),
              days_on_bench: Math.max(0, Math.floor((now - new Date(r.updated_at).getTime()) / 86400000)),
            };
          }),
        );
      } catch (e) {
        if (!cancelled) setError(errorMessage(e, 'Could not load the bench.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  return { rows, loading, error, refetch: useCallback(() => setTick((n) => n + 1), []) };
}
